import React, { useEffect, useState } from "react";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { EyeOff, RotateCcw, Save } from "lucide-react";
import { toast } from "sonner";

export default function AdminSettingsPage() {
  const { billVisibility, today, refreshBoot } = useApp();
  const [val, setVal] = useState(billVisibility?.visible_from || "");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setVal(billVisibility?.visible_from || ""); }, [billVisibility?.visible_from]);

  const save = async (v) => {
    setBusy(true);
    try {
      await api.patch("/settings", { bills_visible_from: v });
      await refreshBoot();
      toast.success(v ? `Cashiers & accountants now see bills from ${fmtDate(v)} onward` : "Reset to rolling 1-week window");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-xl font-semibold arch-underline">Settings</h1>
        <p className="text-sm text-muted-foreground">Business-wide controls</p>
      </div>

      <Card className="rounded-lg">
        <CardContent className="p-4 space-y-4" data-testid="bill-visibility-card">
          <div className="flex items-start gap-3">
            <div className="h-9 w-9 rounded bg-[hsl(var(--brass))]/15 flex items-center justify-center shrink-0">
              <EyeOff className="h-4 w-4 text-[hsl(var(--brass))]" />
            </div>
            <div>
              <h2 className="text-base md:text-lg font-semibold">Bill visibility for cashiers &amp; accountants</h2>
              <p className="text-sm text-muted-foreground">
                Hide bills before a date. Cashiers and accountants can view (never edit) back-dated bills only from this date onward.
                Managers and admin are never restricted.
              </p>
            </div>
          </div>

          <div className="rounded border bg-secondary/40 px-3 py-2 text-sm" data-testid="bill-visibility-status">
            Currently visible from <b className="font-mono-num">{billVisibility ? fmtDate(billVisibility.visible_from) : "—"}</b>
            {billVisibility?.is_default
              ? <span className="text-muted-foreground"> · default rolling {billVisibility.default_days}-day window</span>
              : <span className="text-[hsl(var(--brass))]"> · fixed date set by admin</span>}
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1.5">
              <Label>Hide bills before</Label>
              <Input type="date" value={val} max={today} onChange={(e) => setVal(e.target.value)}
                className="h-10 w-[170px]" data-testid="bill-visibility-date-input" />
            </div>
            <Button onClick={() => save(val)} disabled={busy || !val || val === billVisibility?.visible_from}
              className="h-10 bg-primary hover:bg-[hsl(var(--sapphire-2))]" data-testid="bill-visibility-save-button">
              <Save className="h-3.5 w-3.5 mr-1.5" />Save date
            </Button>
            <Button variant="outline" onClick={() => save("")} disabled={busy || billVisibility?.is_default}
              className="h-10" data-testid="bill-visibility-reset-button">
              <RotateCcw className="h-3.5 w-3.5 mr-1.5" />Reset to rolling 1 week
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
